import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ErrorEvent, LoadingEvent, UIContextEvent, WarsEvents } from '@playroomkit/pr-wars/events';
import { I18nextProvider } from 'react-i18next';
import { inittI18n } from './i18n';
import { i18n } from 'i18next';
import { AuthProvider } from './contexts/AuthProvider';
import { LoadingProvider } from './contexts/LoadingProvider';
import { UrlMapper } from '@playroomkit/pr-common';
import { URLMapperContext } from './contexts/URLMapperContext';
import { UIContextType } from '@playroomkit/pr-wars-data';
import GameContent from '@components/GameContent/GameContent';
import './App.css';

function App() {
  const [error, setError] = useState<string | null>(null);
  const [showLoadingBar, setShowLoadingBar] = useState<boolean>(false);
  const [showNoAccess, setShowNoAccess] = useState(false);
  const [percentage, setPercentage] = useState(0);

  const fakeTimerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [urlMapper, setUrlMapper] = useState<UrlMapper | null>(null);
  const [i18nInstance, seti18nInstance] = useState<i18n>();
    
  useLayoutEffect(() => {

    document.addEventListener(WarsEvents.ErrorEvent, handleErrorEvent);
    document.addEventListener(WarsEvents.LoadingEvent, handleLoadingMessage);

    return () => {
      document.removeEventListener(WarsEvents.ErrorEvent, handleErrorEvent);
      document.removeEventListener(WarsEvents.LoadingEvent, handleLoadingMessage);
    }
  }, []);

  useEffect(() => {
    
    const urlMapper: UrlMapper = new UrlMapper([
      { prefix: '/__mix', target: import.meta.env.VITE_DCD_MIXPANEL_TARGET },
      { prefix: '/_pr_api', target: import.meta.env.VITE_DCD_PR_API_TARGET },
      { prefix: '/_pr_content', target: import.meta.env.VITE_DCD_PR_CONTENT_TARGET },
      { prefix: '/_game', target: import.meta.env.VITE_DCD_GAME_BACKEND_TARGET },
      { prefix: '/_cf-content', target: import.meta.env.VITE_DCD_CLOUDFRONT_TARGET },
      { prefix: '/_content', target: import.meta.env.VITE_DCD_CONTENT_TARGET },
      { prefix: '/_config', target: import.meta.env.VITE_DCD_CONFIG_SERVICE_TARGET },
      { prefix: '/_ws', target: import.meta.env.VITE_DCD_PR_WS_TARGET }
    ]);
  
    setUrlMapper(urlMapper);
  
    // Setup i18n
    inittI18n(urlMapper).then((instance) => {
      
      // Set the instance
      seti18nInstance(instance);
    })

    const handleUIContextEvent = (event: Event) => {
      const e = event as UIContextEvent,
      display = e.hasContext(UIContextType.NOACCESS);
      setShowNoAccess(display);
      if (display) {
        setShowLoadingBar(false);
      }
    };

    document.addEventListener(WarsEvents.UIContextEvent, handleUIContextEvent);
    
    return () => {
      document.removeEventListener(WarsEvents.UIContextEvent, handleUIContextEvent);
    };
  }, [showNoAccess]);

  useEffect(() => () => {
    if (fakeTimerIntervalRef.current) {
      clearInterval(fakeTimerIntervalRef.current)
    }
  }, [])

  const handleErrorEvent = (event: Event) => {
    let e = event as ErrorEvent;

    setError(e.message);
    setTimeout(() => setError(null), e.duration);
  };

  const handleLoadingMessage = (event: Event) => {
    let e = event as LoadingEvent;
    
    if(e.percentage >= 100 || e.percentage < 0) {
      setShowLoadingBar(false);
      return;
    }

    setShowLoadingBar(true);

    // Play fake loading from the start
    if (e.fake) {
      setPercentage(e.percentage);
      handleFakeLoadingState();
    }
    else {
      setPercentage(e.percentage);
    }
  };

  const handleFakeLoadingState = () => {
    fakeTimerIntervalRef.current = setInterval(() => {
      setPercentage((prev) => (prev < 95 ? prev + 10 : 95));
    }, 250);
  }

  if (!urlMapper || !i18nInstance) {
    return <div>Loading…</div>;
  }

  return (
    <URLMapperContext.Provider value={urlMapper}>
      <I18nextProvider i18n={i18nInstance}>
        <AuthProvider>
            <LoadingProvider>
                <GameContent {...{error, percentage, showLoadingBar, showNoAccess}} />
            </LoadingProvider>
        </AuthProvider>
      </I18nextProvider>
    </URLMapperContext.Provider>
  );
}

export default App;